import React, { useState } from 'react';
import { IconX, IconArrowRight } from '../icons';
import { useStore } from '../../store/StoreContext';
import { formatTime12 } from '../../utils/formatters';
import { getTripTag, jobResourceStatus } from '../../utils/dispatchHelpers';
import { DEFAULT_OPERATIONS_STATUSES } from '../../lib/constants';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import { TimePicker, NumberField, ChoiceKeys, toKeyItems, Button, IconButton } from '../ui';
import { kindSegments, kindIsPackOnly } from '../../lib/phaseKinds';
import { fmtMoveDate } from '../../lib/moves';

// Which of the move's three dates does a dispatch leg ride on, and is that
// date still tentative? (moves rule 3, 2026-09-18.) Pack-only legs follow
// the pack date; anything on the unload side (unload / return, incl. the
// drive+unload combos) follows the unload date when the move has one;
// everything else — MOVE, LOAD, DRIVE, HOLD, small-job legs — is the load
// day. Shared by the board pill (dashed amber edge) and this popup (~date).
export function jobTentativeFlag(job, move) {
  if (!job || !move || job.type === 'WALKTHROUGH') return false;
  const key = jobDateKey(job, move);
  return !!move[`${key}_tentative`];
}

// Which move date key ('pack' | 'load' | 'unload') a dispatch leg rides on.
// Same rule as above, exported on its own so the board's change-date dialog
// can flip the RIGHT tentative flag after a drop (2026-09-18). `move` is
// optional — without one an unload-side leg still answers 'unload'.
export function jobDateKey(job, move = null) {
  const kind = job?.phase_kind || 'move';
  if (kindIsPackOnly(kind)) return 'pack';
  const segs = kindSegments(kind);
  const unloadSide = segs.some(s => s === 'unload' || s === 'return') && !segs.includes('load') && !segs.includes('move');
  if (unloadSide && (!move || move.unload_date)) return 'unload';
  return 'load';
}

const inputCls = 'border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full';

// `align` (2026-09-25): 'right' hangs the popup off the pill's right edge —
// for pills in the board's right-hand columns, where a left-anchored popup
// ran past the grid's edge and got clipped by its scroll container.
export default function DispatchPillExpanded({ job, client, isSpanDay, dateStr, spanDayNum, getCrew, getTruck, getTripTag, getLegCrew, estimates, allJobs, onClose, onEditAssignment, onSendSms, onNavigateToClient, saveJob, showToast, align = 'left' }) {
    // patchClient for the unified walkthrough Complete (2026-08-07) —
    // pulled from the store rather than threading a prop through the
    // board (same pattern as AssignModal's availability). patchClient,
    // NOT saveClient: partial rows through the full upsert hit the
    // NOT NULL insert path (store comment on patchClient).
    const { patchClient, trucks, moves } = useStore();
  // The leg's move (moves, 2026-09-18) — only for the tentative "~" marker;
  // details below still read the job / estimate mirror columns.
  const linkedMove = job.move_id ? (moves || []).find(m => m.id === job.move_id) : null;
  const dateTentative = jobTentativeFlag(job, linkedMove);
  const [editMode, setEditMode] = useState(false);
  const [warnDispatch, setWarnDispatch] = useState(false);
  // In-app confirms (QA M2 NEW-9 — the natives here froze the board
  // invisibly under automation, same class as JobModal's NEW-4).
  const { confirm, confirmElement } = useConfirm();
  // Dispatch = open the send-confirm modal (2026-07-29 button
  // reconciliation: the old status-only Dispatch button and the SMS
  // button merged into one action — the confirm modal carries a "mark
  // dispatched without texting" secondary for the old behavior).
  // Warn first (non-blocking) if crew/trucks aren't fully assigned vs
  // the estimate's requirements.
  const handleDispatchClick = () => {
    if (reqStatus && !reqStatus.allMet) setWarnDispatch(true);
    else onSendSms();
  };
  const [form, setForm] = useState({ time: job.time, duration: job.duration, day_sequence: job.day_sequence || 1, dispatcher_notes: job.dispatcher_notes || '', job_label: job.job_label || '', location_name: job.location_name || '', status: job.status });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const linkedEstimate = estimates.find(e => e.id === job.estimate_id);
  const reqStatus = linkedEstimate ? jobResourceStatus(job, linkedEstimate, trucks) : null;
  // ✓ green when a requirement is met, ○ amber when still short.
  const ReqMark = ({ met }) => (
    <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold flex-shrink-0 ${met ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'}`} aria-hidden="true">
      {met ? '✓' : '!'}
    </span>
  );

  // Sibling phases in the same estimate (for "Unassign All Phases")
  const siblingJobs = (allJobs || []).filter(j =>
    j.id !== job.id &&
    j.estimate_id &&
    j.estimate_id === job.estimate_id
  );
  const hasSiblings = siblingJobs.length > 0;

  const handleUnassignAllPhases = async () => {
    const allPhaseJobs = [job, ...siblingJobs];
    const clientName = client?.name || 'this job';
    if (!(await confirm({
      title: `Unassign all ${allPhaseJobs.length} phases for ${clientName}?`,
      body: `Crew and truck come off every phase for ${clientName} and all ${allPhaseJobs.length} move back to the unassigned pool. The phases, estimate and notes are kept.`,
      confirmLabel: 'Unassign all phases',
    }))) return;
    try {
      // The anchor phase (LOAD for BOL, MOVE for local) keeps the estimate's
      // move_date so it reappears on the correct day in the unassigned row,
      // just like a fresh push-to-dispatch. All other phases go dateless.
      const anchorKinds = new Set(['load', 'move']);
      const estMoveDate = linkedEstimate?.move_date || null;
      for (const j of allPhaseJobs) {
        const isAnchor = anchorKinds.has(j.phase_kind);
        await saveJob({
          ...j,
          crew_ids: [],
          leader_id: null,
          truck_ids: [],
          status: 'unassigned',
          move_date: isAnchor ? estMoveDate : null,
        });
      }
      showToast(`Unassigned all ${allPhaseJobs.length} phases for ${clientName}`);
      onClose();
    } catch(e) { showToast('Error: ' + e.message, 'error'); }
  };

  const handleSaveDispatchFields = async () => {
    setSaving(true);
    try {
      const status = (job.crew_ids || []).length > 0 && form.status === 'unassigned' ? 'assigned' : form.status;
      await saveJob({ ...job, ...form, status });
      showToast('Job updated');
      setEditMode(false);
    } catch(e) { showToast('Error: ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} top-full z-50 bg-white border border-slate-300 rounded-lg shadow-2xl p-4 mt-1 text-slate-900`} style={{ zIndex: 999, width: editMode ? '22rem' : '18rem' }} onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-sm flex items-center gap-2">
          {client?.name}
        </div>
        <IconButton label="Close" size="sm" className="-my-1 -mr-1" onClick={onClose}><IconX size={14}/></IconButton>
      </div>

      <div className="text-xs space-y-1 mb-3 bg-slate-50 rounded-lg p-2.5 border border-slate-100">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold text-slate-400 uppercase">Move Details</span>
          {job.client_id && (
            <Button variant="tertiary" size="sm" className="-my-2" rightIcon={<IconArrowRight size={10}/>} onClick={() => onNavigateToClient(job.client_id)}>
              {linkedEstimate ? 'Edit on Estimate' : 'View Client'}
            </Button>
          )}
        </div>
        {linkedEstimate && <div className="text-[10px] text-indigo-500 mb-1">Est #{linkedEstimate.job_number}</div>}
        <div><strong>Type:</strong> {job.job_label || job.type}</div>
        {/* The day this leg is on — fmtMoveDate carries the "~" (moves rule 3):
            amber = the move's date is still tentative. Year shown: this popup
            is where a dispatcher double-checks a far-out trip. */}
        {(job.move_date || dateStr || job.date) && (
          <div><strong>Date:</strong> <span className={dateTentative ? 'text-amber-700 font-semibold' : ''}>{fmtMoveDate(job.move_date || dateStr || job.date, { tentative: dateTentative, year: true })}</span>{dateTentative && <span className="text-amber-600"> · tentative</span>}</div>
        )}
        <div><strong>From:</strong> {job.location_name ? <span className="font-medium">{job.location_name} — </span> : ''}{job.from_address || linkedEstimate?.origin_address || client?.origin_address || <span className="text-slate-400 italic">Not set</span>}</div>
        <div><strong>To:</strong> {job.to_address || linkedEstimate?.destination_address || client?.destination_address || <span className="text-slate-400 italic">Not set</span>}</div>
        {(job.heavy_items || linkedEstimate?.heavy_items) && <div><strong>Heavy:</strong> {job.heavy_items || linkedEstimate?.heavy_items}</div>}
        {job.special_notes && <div><strong>Notes:</strong> {job.special_notes}</div>}
        {(job.stairs || linkedEstimate?.from_stairs) && <div className="text-orange-600">Stairs ({job.levels || linkedEstimate?.from_levels || '?'} levels)</div>}
        {(job.elevator || linkedEstimate?.from_elevator) && <div>Elevator available</div>}
        {linkedEstimate?.from_sqft > 0 && <div><strong>Size:</strong> {linkedEstimate.from_sqft} sq ft</div>}
      </div>

      {editMode ? (
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-[10px] font-bold text-slate-400 uppercase">Dispatch Fields</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 font-bold">Time</label>
              <TimePicker value={form.time || ''} onChange={v => setField('time', v)} ariaLabel="Job time" compact />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-bold">Duration (hrs)</label>
              <NumberField size="sm" step={1} min={1} value={form.duration} onChange={v => setField('duration', parseInt(v) || 1)} ariaLabel="Duration in hours" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 font-bold">Job Label</label>
              <input className={`${inputCls} text-xs`} value={form.job_label} onChange={e => setField('job_label', e.target.value)} placeholder="Storage Move..." />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-bold">Day Order</label>
              <ChoiceKeys
                size="sm"
                ariaLabel="Day order"
                items={[{ value: 1, label: '1st' }, { value: 2, label: '2nd' }, { value: 3, label: '3rd' }, { value: 4, label: '4th' }]}
                value={form.day_sequence}
                onChange={v => setField('day_sequence', parseInt(v))}
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold">Location Name</label>
            <input className={`${inputCls} text-xs`} value={form.location_name} onChange={e => setField('location_name', e.target.value)} placeholder="Extra Space Storage..." />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold">Dispatcher Notes</label>
            <textarea className={`${inputCls} text-xs`} rows="2" value={form.dispatcher_notes} onChange={e => setField('dispatcher_notes', e.target.value)} placeholder="BOL on locker, gate code..." />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 font-bold">Operations Status</label>
            <ChoiceKeys size="sm" ariaLabel="Operations status" items={toKeyItems(DEFAULT_OPERATIONS_STATUSES)} value={form.status} onChange={v => setField('status', v)} />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="primary" size="sm" loading={saving} onClick={handleSaveDispatchFields}>Save</Button>
            <Button variant="secondary" size="sm" onClick={() => { setForm({ time: job.time, duration: job.duration, day_sequence: job.day_sequence || 1, dispatcher_notes: job.dispatcher_notes || '', job_label: job.job_label || '', location_name: job.location_name || '', status: job.status }); setEditMode(false); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="text-xs space-y-1 mb-3">
          <div><strong>Time:</strong> {formatTime12(job.time)} &bull; <strong>Duration:</strong> {job.duration}h</div>
          {job.dispatcher_notes && <div className="text-purple-700"><strong>Dispatch Notes:</strong> {job.dispatcher_notes}</div>}
          <div><strong>Status:</strong> <span className="capitalize">{job.status.replace('_', ' ')}</span></div>
        </div>
      )}

      {/* Requirements from estimate allocation — shown prominently, with a
          ✓ once the assigned crew/trucks meet each requirement. */}
      {linkedEstimate && (
        <div className={`text-xs space-y-1 mb-3 rounded-lg p-2.5 border-2 ${reqStatus?.allMet ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'}`}>
          <div className="flex items-center justify-between mb-1">
            <span className={`text-[11px] font-extrabold uppercase tracking-wide ${reqStatus?.allMet ? 'text-emerald-700' : 'text-amber-700'}`}>Requirements</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${reqStatus?.allMet ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-white'}`}>
              {reqStatus?.allMet ? 'All set ✓' : 'Not fully assigned'}
            </span>
          </div>
          {reqStatus && (reqStatus.requiredTrucks > 0 || reqStatus.requiredCrew > 0) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 mb-1">
              {reqStatus.requiredTrucks > 0 && (
                <span className="inline-flex items-center gap-1">
                  <ReqMark met={reqStatus.trucksMet} />
                  <span className="font-semibold">{reqStatus.packingVehicleRequired ? `📦 Pack vehicle ${reqStatus.trucksMet ? '1' : '0'}/1` : `Trucks ${reqStatus.assignedTrucks}/${reqStatus.requiredTrucks}`}</span>
                </span>
              )}
              {reqStatus.requiredCrew > 0 && (
                <span className="inline-flex items-center gap-1">
                  <ReqMark met={reqStatus.crewMet} />
                  <span className="font-semibold">{reqStatus.packingVehicleRequired ? 'Packers' : 'Crew'} {reqStatus.assignedCrew}/{reqStatus.requiredCrew}</span>
                </span>
              )}
            </div>
          )}
          {(() => {
            const est = linkedEstimate;
            // PACK legs (Jensen 2026-08-25): the whole-move truck/crew
            // allocation is noise on a packing day — show only what the
            // packing crew actually needs.
            if (job.phase_kind === 'pack') {
              const packers = Number(job.crew_size) || Number(est.packing_crew_size) || 0;
              return (
                <>
                  <div><strong>Vehicle:</strong> Any packing vehicle (16' truck or pack van)</div>
                  {packers > 0 && <div><strong>Packing Crew:</strong> {packers} packer{packers !== 1 ? 's' : ''}</div>}
                </>
              );
            }
            // Build truck-requirement list from BOTH trucks_needed (the
            // primary, stored as a size label like '26ft') AND
            // extra_trucks (a JSON array of additional size labels).
            // Previously this view only rendered the primary, which is
            // why a "26' + 16'" estimate appeared to drop the 16' on
            // the dispatch pill.
            const labelFor = (raw) => ({
              'none': 'No Truck (Labor Only)',
              '16ft': "16' Truck",
              '26ft': "26' Truck",
              '2x26ft': "2x 26' Trucks",
            }[raw] || raw || '');
            const primary = labelFor(est.trucks_needed);
            let extras = [];
            try {
              const arr = JSON.parse(est.extra_trucks || '[]');
              if (Array.isArray(arr)) extras = arr.map(labelFor).filter(Boolean);
            } catch { /* ignore */ }
            const truckLabels = [primary, ...extras].filter(Boolean);
            const isOOS = est.job_type === 'LONG_DISTANCE';
            return (
              <>
                {truckLabels.length > 0 && (
                  <div>
                    <strong>Truck{truckLabels.length > 1 ? 's' : ''}:</strong>{' '}
                    {truckLabels.join(' + ')}
                  </div>
                )}
                {isOOS ? (
                  <>
                    {(est.crew_size_needed || 0) > 0 && <div><strong>Load Crew:</strong> {est.crew_size_needed} to load</div>}
                    {(est.travel_crew_size || 0) > 0 && <div><strong>Travel Crew:</strong> {est.travel_crew_size} on trip</div>}
                  </>
                ) : (
                  (est.crew_size_needed || 0) > 0 && <div><strong>Crew:</strong> {est.crew_size_needed} movers</div>
                )}
                {(est.packing_crew_size || 0) > 0 && <div><strong>Packing Crew:</strong> {est.packing_crew_size} packers</div>}
              </>
            );
          })()}
        </div>
      )}

      <div className="text-xs mb-2"><strong>{isSpanDay ? `Trip Crew (${getTripTag(job.trip_tags, dateStr) || 'Day ' + spanDayNum}):` : 'Crew:'}</strong>
        {isSpanDay ? (
          <div className="flex flex-wrap gap-1 mt-1">
            {(getLegCrew(job.trip_tags, dateStr) || job.crew_ids || []).map(cid => {
              const c = getCrew(cid);
              return <span key={cid} className="px-2 py-0.5 rounded text-white text-xs" style={{backgroundColor: c?.display_color || '#888'}}>{c?.name}</span>;
            })}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1 mt-1">{(job.crew_ids || []).map(cid => { const c = getCrew(cid); return <span key={cid} className="px-2 py-0.5 rounded text-white text-xs" style={{backgroundColor: c?.display_color || '#888'}}>{c?.name}{cid === job.leader_id ? ' (L)' : ''}</span>; })}</div>
        )}
      </div>

      {(job.truck_ids || []).length > 0 && (
        <div className="text-xs mb-2"><strong>Truck:</strong>
          <div className="flex flex-wrap gap-1 mt-1">
            {job.truck_ids.map(tid => {
              const truck = typeof getTruck === 'function' ? getTruck(tid) : null;
              return <span key={tid} className="px-2 py-0.5 rounded bg-slate-700 text-white text-xs font-semibold">{truck?.name || 'Truck'}</span>;
            })}
          </div>
        </div>
      )}

      {job.recap_submitted_at && (
        <div className="text-xs bg-emerald-50 rounded-lg p-2 mb-2 border border-emerald-100">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-bold text-emerald-600 uppercase">Crew Recap</span>
            {job.customer_rating && <span className="text-amber-500">{'★'.repeat(job.customer_rating)}{'☆'.repeat(5 - job.customer_rating)}</span>}
          </div>
          {job.actual_start_time && <div><strong>Actual:</strong> {formatTime12(job.actual_start_time)}{job.actual_end_time ? ` — ${formatTime12(job.actual_end_time)}` : ''}{job.actual_hours ? ` (${job.actual_hours}h)` : ''}</div>}
          {job.crew_recap_notes && <div className="text-slate-600 mt-0.5">{job.crew_recap_notes}</div>}
          {job.damage_notes && <div className="text-red-600 mt-0.5 font-medium">Damage: {job.damage_notes}</div>}
        </div>
      )}

      {job.ops_detail && (
        <div className="text-xs mb-2">
          <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full font-bold capitalize">{job.ops_detail.replace(/_/g, ' ')}</span>
        </div>
      )}

      {job.type === 'WALKTHROUGH' ? (
        <div className="flex flex-wrap gap-2 mt-3">
          {/* UNIFIED COMPLETE (Jaylen 2026-08-07): the estimator app and
              this button used to write DIFFERENT records — the app stamps
              the CLIENT (walkthrough_status + completed_at/by, which
              drives the client lists and the pill's ✓ Done), while this
              only flipped the JOB row (old rotated COMPLETED stamp).
              Now: one done-state (either record counts), and this button
              writes BOTH so the client funnels correctly no matter which
              side marked it. */}
          {(job.status === 'completed' || client?.walkthrough_status === 'completed') && (
            <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded font-bold">
              ✓ Walkthrough completed{client?.walkthrough_completed_by ? ` — ${client.walkthrough_completed_by}` : ''}
            </span>
          )}
          <Button variant="primary" size="sm" onClick={() => onEditAssignment('crew')}>Crew Assignment</Button>
          <Button variant="secondary" size="sm" onClick={() => onEditAssignment('truck')}>Truck Assignment</Button>
          {job.status !== 'completed' && client?.walkthrough_status !== 'completed' && (
            <Button variant="success" size="sm" onClick={async () => {
              try {
                await saveJob({ ...job, status: 'completed' });
                // Mirror the estimator app's completion write (partial
                // upsert, same fields) so both records agree.
                if (client?.id && client.walkthrough_status !== 'completed') {
                  await patchClient(client.id, {
                    walkthrough_status: 'completed',
                    walkthrough_completed_at: new Date().toISOString(),
                    walkthrough_completed_by: client.walkthrough_completed_by || 'Office (dispatch board)',
                  });
                }
                showToast('Walkthrough completed');
                onClose();
              } catch(e) { showToast('Error: ' + e.message, 'error'); }
            }}>✓ Complete</Button>
          )}
          <Button variant="danger" size="sm" onClick={async () => {
            if (await confirm({ title: `Unassign ${client?.name || 'this'}${client?.name ? "'s" : ''} walkthrough?`, body: `The estimator comes off ${client?.name || 'this'}${client?.name ? "'s" : ''} walkthrough and it moves back to the unassigned row. The walkthrough itself and its date are kept.`, confirmLabel: 'Unassign walkthrough' })) {
              try {
                await saveJob({ ...job, crew_ids: [], leader_id: null, truck_ids: [], status: 'unassigned' });
                showToast('Walkthrough unassigned');
                onClose();
              } catch(e) { showToast('Error: ' + e.message, 'error'); }
            }
          }}>Unassign</Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mt-3">
          <Button variant="secondary" size="sm" onClick={() => onEditAssignment('crew')}>Crew Assignment</Button>
          <Button variant="secondary" size="sm" onClick={() => onEditAssignment('truck')}>Truck Assignment</Button>
          <Button variant="primary" size="sm" onClick={handleDispatchClick}>Dispatch</Button>
          {!editMode && <Button variant="secondary" size="sm" onClick={() => setEditMode(true)}>Edit Dispatch</Button>}
          <Button variant="danger" size="sm" onClick={async () => {
            if (await confirm({ title: `Unassign ${client?.name || 'this job'}?`, body: `Crew and truck come off ${client?.name || 'this job'}${client?.name ? "'s job" : ''} and it moves back to the Unassigned row. The job, its date and notes are kept.`, confirmLabel: 'Unassign' })) {
              try {
                await saveJob({ ...job, crew_ids: [], leader_id: null, truck_ids: [], status: 'unassigned' });
                showToast('Job unassigned');
                onClose();
              } catch(e) { showToast('Error: ' + e.message, 'error'); }
            }
          }}>Unassign</Button>
          {hasSiblings && (
            <Button variant="danger" size="sm" onClick={handleUnassignAllPhases}>
              Unassign All Phases
            </Button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={warnDispatch}
        title={`Dispatch ${client?.name || 'this job'} short-staffed?`}
        confirmLabel="Send anyway"
        cancelLabel="Go back"
        onConfirm={() => { setWarnDispatch(false); onSendSms(); }}
        onCancel={() => setWarnDispatch(false)}>
        {client?.name || 'This job'} still needs {reqStatus?.missing.join(' and ')} vs the estimate. The crew assigned so far get texted now; you can add the rest afterwards.
      </ConfirmDialog>
      {confirmElement}
    </div>
  );
}
